import Vue from "vue";
import Router from "vue-router";
import Auth from "@/services/auth.service";
import store from "./stores/index";

/**
 * View Components
 */

// App side
import Home from "./views/Home.vue";
import SignIn from "./views/SignIn.vue";
import ForgotPassword from "./views/ForgotPassword.vue";
import VerifyCode from "./views/VerifyCode.vue";
import OrderDetail from "./views/OrderDetail.vue";
import OrderConfirm from "./views/OrderConfirm.vue";
import OrderFormStep1 from "./components/OrderFormStep1.module.vue";
import OrderFormStep4 from "./components/OrderFormStep4.module.vue";
import OrderFormStep5 from "./components/OrderFormStep5.module.vue";
import OrderFormStep6 from "./components/OrderFormStep6.module.vue";
import OrderFormStep7 from "./components/OrderFormStep7.module.vue";
import OrderFormSelectProduct from "./components/OrderFormSelectProduct.module.vue";
import NotFound from "./components/404.vue";
import ReviseCharacter from "./components/OrderFormReviseCharacter.module.vue";

// Admin side
import AdminHome from "./views/admin/AdminHome.vue";
import AdminUsers from "./views/admin/AdminUsers.vue";
import AdminUserEdit from "./views/admin/AdminUserEdit.vue";
import AdminUserCreate from "./views/admin/AdminUserCreate.vue";
import AdminUserDetail from "./views/admin/AdminUserDetail.vue";
import AdminOrderDetail from "./views/admin/AdminOrderDetail.vue";
import AdminOrderPrintPreview from "./views/admin/AdminOrderPrintPreview.vue";
import AdminApprovalSetPrint from "./views/admin/AdminApprovalSetPrint.vue";
import AdminDashBoard from "./views/admin/AdminDashboard.vue";
import AdminNotification from "./views/admin/AdminNotification.vue";
import AdminNotificationCreate from "./views/admin/AdminNotificationCreate.vue";
import AdminOrderMessage from "./views/admin/AdminOrderMessage.vue";
import AdminClients from "./views/admin/AdminClients.vue";
import AdminVariantCharacterGroups from "./views/admin/AdminVariantCharacterGroups.vue";
import AdminOrderCustomField from "./views/admin/AdminOrderCustomField.vue";
import AdminClientsProductsManagement from "./views/admin/AdminClientsProductsManagement.vue";

// Auth
const authService = new Auth();
Vue.use(Router);

const originalPush = Router.prototype.push;
Router.prototype.push = function push (location) {
  return originalPush.call(this, location).catch(err => err);
};

const ORDER_CREATE_META = {
  header: false,
  headerLeft: "CANCEL_ORDER",
};

const localData = JSON.parse(localStorage.getItem("vuex"));

const children = [
  {
    path: "step1",
    component: OrderFormStep1,
    meta: {
      ...ORDER_CREATE_META,
      title: "ステップ1 - お客様情報の入力",
      userAuth: true,
    },
  },
  {
    path: "step2",
    component: OrderFormSelectProduct,
    meta: {
      ...ORDER_CREATE_META,
      title: "ステップ2 - 商品の選択",
      userAuth: true,
    },
  },
  {
    path: "step4",
    component: OrderFormStep4,
    meta: {
      ...ORDER_CREATE_META,
      title: "ステップ4 - レイアウト",
      userAuth: true,
    },
  },
  {
    path: "step5",
    component: OrderFormStep5,
    meta: {
      ...ORDER_CREATE_META,
      title: "ステップ5 - 彫り/書き・書式",
      userAuth: true,
    },
  },
  {
    path: "step6",
    component: OrderFormStep6,
    meta: {
      ...ORDER_CREATE_META,
      title: "ステップ6- 詳細情報入力",
      userAuth: true,
    },
  },
  {
    path: "revise-character",
    component: ReviseCharacter,
    meta: {
      ...ORDER_CREATE_META,
      title: "ステップ7- 異体字の選択",
    },
  },
  {
    path: "step7",
    component: OrderFormStep7,
    meta: {
      ...ORDER_CREATE_META,
      title: "ステップ7- 詳細情報入力",
      userAuth: true,
    },
  },
];

const AppRoutes = [
  {
    path: "/",
    redirect: () => {
      if (localData && localData.user.sub !== "") {
        if (localData.user.role === "ADMIN") {
          return "/admin/dashboard";
        } else {
          return "/home";
        }
      } else {
        return "/sign-in";
      }
    },
    component: () => {
      if (localData.user.sub !== "") {
        if (localData.user.role === "ADMIN") {
          return AdminDashBoard;
        } else {
          return Home;
        }
      } else {
        return SignIn;
      }
    },
  },
  {
    path: "/sign-in",
    name: "sign-in",
    component: SignIn,
    meta: {
      header: false,
    },
  },
  {
    path: "/not-found",
    name: "not-found",
    component: NotFound,
  },
  {
    path: "/forgot-password",
    name: "forgot-password",
    component: ForgotPassword,
    meta: {
      header: false,
    },
  },
  {
    path: "/verify",
    name: "verify",
    component: VerifyCode,
    meta: {
      header: false,
    },
  },
  {
    path: "/home",
    name: "home",
    component: Home,
    meta: {
      userAuth: true,
    },
  },
  {
    path: "/orders/:id([a-zA-Z0-9-]+)",
    name: "order-detail",
    component: OrderDetail,
    meta: {
      header: false,
      userAuth: true,
    },
  },
  {
    path: "/orderconfirm",
    name: "orderconfirm",
    component: OrderConfirm,
    meta: {
      header: false,
      userAuth: true,
    },
  },
  {
    path: "/orders/create",
    name: "order-create",
    meta: {
      userAuth: true,
    },
    component: () => {
      return import(/* webpackChunkName: "order-create" */ "./views/OrderCreate.vue");
    },
    children,
  },
  {
    path: "/orders/:id/edit",
    name: "order-edit",
    meta: {
      userAuth: true,
    },
    component: () =>
      import(/* webpackChunkName: "order-edit" */ "./views/OrderCreate.vue"),
    children,
  },
  {
    path: "/manage-favorite-variant-characters",
    name: "manage-favorite-variant-characters",
    meta: {
      userAuth: true,
    },
    component: () => {
      return import(/* webpackChunkName: "manage-favorite-variant-characters" */ "./views/ManageFavoriteVariantCharacters.vue");
    },
  },
];

const AdminRoutes = [
  {
    path: "/admin/home",
    name: "admin/home",
    component: AdminHome,
    meta: {
      adminAuth: true,
    },
  },
  {
    path: "/admin/clients",
    name: "admin/clients",
    component: AdminClients,
    meta: {
      adminAuth: true,
    },
  },
  {
    path: "/admin/users/:id",
    name: "admin/users/edit",
    component: AdminUserDetail,
    meta: {
      adminAuth: true,
    },
  },
  {
    path: "/admin/users/create",
    name: "admin/users/create",
    component: AdminUserDetail,
    meta: {
      adminAuth: true,
    },
  },
  {
    path: "/admin/dashboard",
    name: "admin/dashboard",
    component: AdminDashBoard,
    meta: {
      adminAuth: true,
    },
  },
  {
    path: "/admin/notifications",
    name: "admin/notifications",
    component: AdminNotification,
    meta: {
      adminAuth: true,
    },
  },
  {
    path: "/admin/notification/create",
    name: "admin/notification/create",
    component: AdminNotificationCreate,
    meta: {
      adminAuth: true,
    },
  },
  {
    path: "/admin/clients/:clientID/users",
    name: "admin/clients/users",
    component: AdminUsers,
    meta: {
      adminAuth: true,
    },
  },
  {
    path: "/admin/clients/:clientID/productsManagement",
    name: "admin/clients/productsManagement",
    component: AdminClientsProductsManagement,
    meta: {
      adminAuth: true,
    },
  },
  {
    path: "/admin/clients/:clientID/users/create",
    name: "admin/clients/users/create",
    component: AdminUserCreate,
    meta: {
      adminAuth: true,
    },
  },
  {
    path: "/admin/clients/:clientID/users/:id/update",
    name: "admin/clients/users/edit",
    component: AdminUserEdit,
    meta: {
      adminAuth: true,
    },
  },
  {
    path: "/admin/clients/:clientID/order-custom-field",
    name: "admin/clients/order-custom-field",
    component: AdminOrderCustomField,
    meta: {
      adminAuth: true,
    },
  },
  {
    path: "/admin/orders/:id([a-zA-Z0-9-]+)",
    name: "admin-order-detail",
    component: AdminOrderDetail,
    meta: {
      adminAuth: true,
    },
  },
  {
    path: "/admin/orderPrintPreview/:id([a-zA-Z0-9-]+)/:orderNo([0-9]+)",
    name: "admin-order-print-preview",
    component: AdminOrderPrintPreview,
    meta: {
      adminAuth: true,
    },
  },
  {
    path: "/admin/approvalSetPrint/:id([a-zA-Z0-9-]+)/:orderNo([0-9]+)",
    name: "admin-approval-set-print",
    component: AdminApprovalSetPrint,
    meta: {
      adminAuth: true,
    },
  },
  {
    path: "/admin/orders/:id([a-zA-Z0-9-]+)/messages",
    name: "admin-order-message",
    component: AdminOrderMessage,
    meta: {
      adminAuth: true,
    },
  },
  {
    path: "/admin/variantCharacterGroups",
    name: "admin/variantCharacterGroups",
    component: AdminVariantCharacterGroups,
    meta: {
      adminAuth: true,
    },
  },
];

export const router = new Router({
  routes: [
    ...AppRoutes,
    ...AdminRoutes,
    {
      path: "*",
      component: NotFound,
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  try {
    // 認証が不要な画面に関してはそのまま表示
    if (to.name === "sign-in" || to.name === "not-found") {
      next();
    }
    // 認証情報を取得
    const role = await authService.getCurrentUserRole();
    if (!role) {
      // roleが存在しないということは、つまり未認証
      throw new Error("ログインが必要です。");
    }
    if (to.matched.some(record => record.meta.adminAuth)) {
      // 一般ユーザーは管理画面を閲覧できない
      if (role === "CLIENT_USER" || role === "CLIENT_CONTRIBUTOR") {
        next("not-found");
      }
    } else if (to.matched.some(record => record.meta.userAuth)) {
      // Adminユーザーはアプリ画面を閲覧できない
      if (role === "ADMIN") {
        next("/not-found");
      }
    }
    next();
  } catch (err) {
    // 認証が正常に行えないため、サインイン画面に遷移する
    next("sign-in");
  }
});

router.afterEach((to, from) => {
  if (from.name !== null) {
    // 画面遷移を行ったので、URLを保持
    store.commit("ui/setDefault", { url: to.path });
  }
});
